import { useEffect, useState } from "react";
import { Badge } from "@svo/design-system";
import type { SlideProps } from "@/engine/app/offsite-config";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import type { Output } from "@offsite/shared";
import { teamLabel } from "@/engine/app/team-label";

/**
 * Dynamische Reveal-Folie: lädt die Live-Lauf-Outputs via
 * `api.overnight.outputs(token)` und blättert mit ← / → hindurch.
 * (Übernommen aus dem Vertriebs-Deck, Slide25Reveal.)
 */
export function Slide17Reveal(_: SlideProps) {
  const { state } = useStore();
  const [outputs, setOutputs] = useState<Output[]>([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.overnight.outputs(state.auth.token);
        if (!cancelled) setOutputs(res.outputs);
      } catch {
        /* noop */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.auth.token]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowRight") setIdx((i) => Math.min(outputs.length - 1, i + 1));
      else if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [outputs.length]);

  const current = outputs[idx];

  return (
    <div className="deck-root dark" style={{ background: "var(--svo-dunkelblau)" }}>
      <div className="deck-bg" aria-hidden="true" />

      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1">Reveal</div>
        <h1 className="deck-title rise rise-2">
          Die KI hat <br />
          <span className="accent">geliefert</span>
        </h1>
        <p className="deck-sub rise rise-3">
          Gruppe {outputs.length ? idx + 1 : "…"} von {outputs.length || "…"}
        </p>
      </header>

      <div
        className="rise rise-4"
        style={{
          flex: 1,
          background: "#fff",
          border: "1px solid var(--svo-grau-100)",
          borderRadius: "var(--radius-2xl)",
          padding: 24,
          overflow: "auto",
          display: "flex",
        }}
      >
        {current ? (
          <OutputPreview output={current} />
        ) : (
          <div
            style={{
              color: "var(--svo-grau-500)",
              textAlign: "center",
              margin: "auto",
              fontSize: "1.1rem",
            }}
          >
            Noch keine Outputs verfügbar.
          </div>
        )}
      </div>

      <footer className="deck-foot">
        <span style={{ fontStyle: "italic" }}>Pfeiltasten ← → zum Durchklicken</span>
        {current && (
          <Badge variant="blau" size="md">
            {teamLabel(current.teamNumber)}
            {current.teamName ? ` · ${current.teamName}` : ""}
          </Badge>
        )}
      </footer>
    </div>
  );
}

function OutputPreview({ output }: { output: Output }) {
  if (output.contentType === "html") {
    return (
      <iframe
        title={`Output Team ${output.teamNumber}`}
        srcDoc={output.content}
        sandbox="allow-scripts"
        style={{
          width: "100%",
          height: "100%",
          minHeight: "60vh",
          border: "none",
          borderRadius: 8,
          background: "#fff",
        }}
      />
    );
  }
  return (
    <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0, width: "100%" }}>
      {output.content}
    </pre>
  );
}
